import React, { useState, useEffect } from "react";
import { useParams, useLocation, Link, useNavigate } from "react-router-dom";
import TopBar from "../../global/TopBar";
import { StarIcon, ShoppingCartIcon, MapPinIcon, PhoneIcon, ClockIcon, XMarkIcon, EyeIcon, CogIcon, ArrowLeftIcon, TrashIcon, PlusIcon, MinusIcon } from "@heroicons/react/20/solid";
import { motion, AnimatePresence } from "framer-motion";
import Checkout from './Checkout';
import { useSelector } from 'react-redux';

const Restaurant = () => {
  const { restaurantId } = useParams();
  const location = useLocation();
  const [restaurant, setRestaurant] = useState(null);
  const [cart, setCart] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isCheckout, setIsCheckout] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [itemCounts, setItemCounts] = useState({});
  const navigate = useNavigate();
  const [showBackConfirmation, setShowBackConfirmation] = useState(false);
  const token = useSelector((state) => state.auth.token);
  useEffect(() => {
    const fetchRestaurantData = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(`${process.env.REACT_APP_FOOD_API_URL}/restaurant/${restaurantId}`,
        {
          headers: {
            "Content-Type": "application/json",
            "Api-Name": "food-api",
            "Authorization": `Bearer ${token}`
          }
        });
        if (!response.ok) {
          throw new Error('Failed to fetch restaurant data');
        }
        const data = await response.json();
        setRestaurant(data.restaurant);
        
        let focusedCategory, focusedItem;
        
        if (location.state && location.state.focusedItemId) {
          for (const category of data.restaurant.categories) {
            focusedItem = category.items.find(item => item.id === location.state.focusedItemId);
            if (focusedItem) {
              focusedCategory = category;
              break;
            }
          }
        }
        
        if (focusedCategory) {
          setSelectedCategory(focusedCategory.name);
          // Move the focused item to the beginning of the category's items array
          const updatedCategories = data.restaurant.categories.map(category => {
            if (category.name === focusedCategory.name) {
              const updatedItems = [focusedItem, ...category.items.filter(item => item.id !== focusedItem.id)];
              return { ...category, items: updatedItems };
            }
            return category;
          });
          setRestaurant({ ...data.restaurant, categories: updatedCategories });
        } else {
          // Set the first category as default if no focused item
          setSelectedCategory(data.restaurant.categories[0].name);
        }

        // Initialize item counts
        const initialCounts = {};
        data.restaurant.categories.forEach(category => {
          category.items.forEach(item => {
            initialCounts[item.id] = 0;
          });
        });
        setItemCounts(initialCounts);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRestaurantData();
  }, [restaurantId, location]);

  const addToCart = (item) => {
    const count = itemCounts[item.id];
    if (count > 0) {
      const existingItem = cart.find(cartItem => cartItem.id === item.id);
      if (existingItem) {
        setCart(cart.map(cartItem => 
          cartItem.id === item.id ? { ...cartItem, quantity: cartItem.quantity + count } : cartItem
        ));
      } else {
        setCart([...cart, { ...item, quantity: count }]);
      }
      // Don't reset the count to 0 after adding to cart
      // setItemCounts({ ...itemCounts, [item.id]: 0 });
    }
  };

  const removeFromCart = (itemId) => {
    setCart(cart.filter(item => item.id !== itemId));
    setItemCounts({ ...itemCounts, [itemId]: 0 });
  };

  const updateQuantity = (itemId, newQuantity) => {
    if (newQuantity === 0) {
      removeFromCart(itemId);
    } else {
      setCart(cart.map(item => 
        item.id === itemId ? { ...item, quantity: newQuantity } : item
      ));
    }
  };

  const getTotalPrice = () => {
    return cart.reduce((total, item) => total + item.price * item.quantity, 0).toFixed(2);
  };

  const openItemDetails = (item) => {
    setSelectedItem(item);
  };

  const closeItemDetails = () => {
    setSelectedItem(null);
  };

  const handleBack = () => {
    setShowBackConfirmation(true);
  };

  const confirmBack = () => {
    const savedState = sessionStorage.getItem('foodSearchState');
    if (savedState) {
      const searchState = JSON.parse(savedState);
      navigate('/food', { state: { fromSearch: true, ...searchState } });
    } else {
      navigate('/food');
    }
  };

  const isItemInCart = (itemId) => {
    return cart.some(item => item.id === itemId);
  };

  const incrementCount = (itemId) => {
    setItemCounts({ ...itemCounts, [itemId]: (itemCounts[itemId] || 0) + 1 });
    // Update cart if item is already in cart
    const existingItem = cart.find(item => item.id === itemId);
    if (existingItem) {
      updateQuantity(itemId, existingItem.quantity + 1);
    }
  };

  const decrementCount = (itemId) => {
    if (itemCounts[itemId] > 0) {
      setItemCounts({ ...itemCounts, [itemId]: itemCounts[itemId] - 1 });
      // Update cart if item is already in cart
      const existingItem = cart.find(item => item.id === itemId);
      if (existingItem) {
        updateQuantity(itemId, existingItem.quantity - 1);
      }
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  if (!restaurant) {
    return <div>Restaurant not found</div>;
  }

  if (isCheckout) {
    return (
      <Checkout
        cart={cart}
        total={getTotalPrice()}
        onBack={() => setIsCheckout(false)}
      />
    );
  }

  return (
    <>
      <TopBar />
      <div className="text-white relative min-h-screen bg-[#1a1f2e]">
        {/* Hero Section */}
        <div className="relative h-[50vh] md:h-[60vh]">
          <motion.img 
            initial={{ scale: 1.1, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.6 }}
            src={restaurant.image} 
            alt={restaurant.name} 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1a1f2e] to-transparent" />
          
          {/* Back Button */}
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={handleBack}
            className="absolute top-4 left-4 px-4 py-2 rounded-xl bg-white/10 backdrop-blur-sm
                     border border-white/20 text-white hover:bg-white/20
                     transition-all duration-300 flex items-center gap-2"
          >
            <ArrowLeftIcon className="h-5 w-5" />
            Back
          </motion.button>

          {/* Restaurant Info */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-[#1a1f2e] to-transparent"
          >
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col md:flex-row md:justify-between md:items-end gap-4">
                <div className="space-y-2">
                  <h1 className="text-3xl md:text-5xl font-bold text-white">{restaurant.name}</h1>
                  <div className="flex items-center gap-2">
                    <StarIcon className="h-5 w-5 text-yellow-400" />
                    <span className="text-lg">{restaurant.rating.toFixed(1)}</span>
                  </div>
                  <div className="flex flex-wrap gap-4 text-sm md:text-base">
                    <div className="flex items-center gap-2">
                      <MapPinIcon className="h-5 w-5 text-yellow-400" />
                      <span>{`${restaurant.address.number} ${restaurant.address.street}, ${restaurant.address.city}`}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <PhoneIcon className="h-5 w-5 text-yellow-400" />
                      <span>{restaurant.phone}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <ClockIcon className="h-5 w-5 text-yellow-400" />
                      <span>{restaurant.hours}</span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Link
                    to={`/food/restaurant/${restaurantId}/manage`}
                    className="px-4 py-2 rounded-xl bg-white/10 backdrop-blur-sm
                             border border-white/20 text-white hover:bg-white/20
                             transition-all duration-300 flex items-center gap-2"
                  >
                    <CogIcon className="h-5 w-5" />
                    Manage
                  </Link>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setIsCartOpen(true)}
                    className="px-4 py-2 rounded-xl bg-gradient-to-r from-yellow-400 to-green-400
                             text-gray-900 hover:shadow-lg transition-all duration-300
                             flex items-center gap-2"
                  >
                    <ShoppingCartIcon className="h-5 w-5" />
                    Cart ({cart.reduce((total, item) => total + item.quantity, 0)})
                  </motion.button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Categories Sidebar */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="lg:w-64 flex-shrink-0"
            >
              <h2 className="text-xl font-bold mb-4">Menu Categories</h2>
              <div className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-4 lg:pb-0">
                {restaurant.categories.map((category) => (
                  <motion.button
                    key={category.name}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setSelectedCategory(category.name)}
                    className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap
                              transition-all duration-300 flex-shrink-0
                              ${selectedCategory === category.name 
                                ? 'bg-gradient-to-r from-yellow-400 to-green-400 text-gray-900' 
                                : 'bg-white/10 text-white hover:bg-white/20'}`}
                  >
                    {category.name}
                  </motion.button>
                ))}
              </div>
            </motion.div>

            {/* Menu Items Grid */}
            <div className="flex-1">
              {selectedCategory && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <h2 className="text-2xl font-bold mb-6">{selectedCategory}</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {restaurant.categories
                      .find(cat => cat.name === selectedCategory)
                      .items.map((item) => (
                        <motion.div
                          key={item.id}
                          whileHover={{ scale: 1.02 }}
                          className="bg-white/5 backdrop-blur-sm rounded-xl overflow-hidden
                                   border border-white/10 hover:bg-white/10 transition-all duration-300"
                        >
                          <div className="relative h-48">
                            <img 
                              src={item.image} 
                              alt={item.name} 
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                          </div>

                          <div className="p-4">
                            <div className="flex justify-between items-start gap-4 mb-3">
                              <h3 className="text-lg font-semibold">{item.name}</h3>
                              <p className="text-yellow-400 font-bold">£{item.price.toFixed(2)}</p>
                            </div>
                            <p className="text-gray-400 text-sm mb-4 line-clamp-2">{item.description}</p>
                            
                            <div className="flex items-center justify-between">
                              <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => openItemDetails(item)}
                                className="px-3 py-1.5 rounded-lg bg-white/10 text-white
                                         hover:bg-white/20 transition-all duration-200
                                         flex items-center gap-2 text-sm"
                              >
                                <EyeIcon className="h-4 w-4" />
                                Details
                              </motion.button>

                              <div className="flex items-center gap-2">
                                <motion.button
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                  onClick={() => decrementCount(item.id)}
                                  className="p-1.5 rounded-lg bg-white/10 text-white
                                           hover:bg-white/20 transition-all duration-200"
                                >
                                  <MinusIcon className="h-4 w-4" />
                                </motion.button>
                                <span className="w-8 text-center">{itemCounts[item.id] || 0}</span>
                                <motion.button
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                  onClick={() => incrementCount(item.id)}
                                  className="p-1.5 rounded-lg bg-white/10 text-white
                                           hover:bg-white/20 transition-all duration-200"
                                >
                                  <PlusIcon className="h-4 w-4" />
                                </motion.button>

                                {isItemInCart(item.id) ? (
                                  <motion.button
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    onClick={() => removeFromCart(item.id)}
                                    className="px-3 py-1.5 rounded-lg bg-red-500/20 text-red-400
                                             hover:bg-red-500/30 transition-all duration-200
                                             flex items-center gap-2 text-sm"
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                    Remove
                                  </motion.button>
                                ) : (
                                  <motion.button
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    onClick={() => addToCart(item)}
                                    disabled={itemCounts[item.id] < 1}
                                    className={`px-3 py-1.5 rounded-lg text-sm
                                              flex items-center gap-2
                                              ${itemCounts[item.id] >= 1
                                                ? 'bg-gradient-to-r from-yellow-400 to-green-400 text-gray-900'
                                                : 'bg-white/10 text-gray-400 cursor-not-allowed'}`}
                                  >
                                    <ShoppingCartIcon className="h-4 w-4" />
                                    Add
                                  </motion.button>
                                )}
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </div>

        {/* Item Details Modal */}
        <AnimatePresence>
          {selectedItem && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 overflow-y-auto flex items-center justify-center p-4"
            >
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/80 backdrop-blur-sm"
                onClick={closeItemDetails}
              />

              {/* Modal Content */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="relative bg-[#1a1f2e] rounded-2xl overflow-hidden border border-white/10
                         shadow-xl w-full max-w-2xl my-8"
              >
                {/* Image Section */}
                <div className="relative h-64 sm:h-72">
                  <img 
                    src={selectedItem.image} 
                    alt={selectedItem.name} 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1a1f2e] to-transparent" />
                  
                  {/* Close Button */}
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={closeItemDetails}
                    className="absolute top-4 right-4 p-2 rounded-full bg-white/10 backdrop-blur-sm
                             border border-white/20 text-white hover:bg-white/20
                             transition-all duration-300"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </motion.button>

                  {/* Item Price */}
                  <div className="absolute bottom-4 right-4">
                    <span className="px-4 py-2 rounded-full bg-gradient-to-r from-yellow-400 to-green-400
                                 text-gray-900 font-bold text-lg">
                      £{selectedItem.price.toFixed(2)}
                    </span>
                  </div>
                </div>

                {/* Content Section */}
                <div className="p-6 space-y-6">
                  {/* Title and Description */}
                  <div>
                    <h2 className="text-2xl font-bold text-white mb-2">{selectedItem.name}</h2>
                    <p className="text-gray-400 leading-relaxed">{selectedItem.description}</p>
                  </div>

                  {/* Quantity Controls */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => decrementCount(selectedItem.id)}
                        className="p-2 rounded-xl bg-white/10 text-white hover:bg-white/20
                                 transition-all duration-200"
                      >
                        <MinusIcon className="h-5 w-5" />
                      </motion.button>
                      
                      <span className="text-xl font-semibold text-white w-8 text-center">
                        {itemCounts[selectedItem.id] || 0}
                      </span>
                      
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => incrementCount(selectedItem.id)}
                        className="p-2 rounded-xl bg-white/10 text-white hover:bg-white/20
                                 transition-all duration-200"
                      >
                        <PlusIcon className="h-5 w-5" />
                      </motion.button>
                    </div>

                    {isItemInCart(selectedItem.id) ? (
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => {
                          removeFromCart(selectedItem.id);
                          closeItemDetails();
                        }}
                        className="px-4 py-2 rounded-xl bg-red-500/20 text-red-400
                                 hover:bg-red-500/30 transition-all duration-200
                                 flex items-center gap-2"
                      >
                        <TrashIcon className="h-5 w-5" />
                        Remove from Cart
                      </motion.button>
                    ) : (
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => {
                          addToCart(selectedItem);
                          closeItemDetails();
                        }}
                        disabled={itemCounts[selectedItem.id] < 1}
                        className={`px-4 py-2 rounded-xl flex items-center gap-2
                                 ${itemCounts[selectedItem.id] >= 1
                                   ? 'bg-gradient-to-r from-yellow-400 to-green-400 text-gray-900'
                                   : 'bg-white/10 text-gray-400 cursor-not-allowed'}`}
                      >
                        <ShoppingCartIcon className="h-5 w-5" />
                        Add to Cart
                      </motion.button>
                    )}
                  </div>

                  {/* Nutritional Information */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div className="bg-white/5 rounded-xl p-4 text-center">
                      <div className="text-sm text-gray-400 mb-1">Calories</div>
                      <div className="text-lg font-semibold text-white">500 kcal</div>
                    </div>
                    <div className="bg-white/5 rounded-xl p-4 text-center">
                      <div className="text-sm text-gray-400 mb-1">Protein</div>
                      <div className="text-lg font-semibold text-white">20g</div>
                    </div>
                    <div className="bg-white/5 rounded-xl p-4 text-center">
                      <div className="text-sm text-gray-400 mb-1">Carbs</div>
                      <div className="text-lg font-semibold text-white">60g</div>
                    </div>
                    <div className="bg-white/5 rounded-xl p-4 text-center">
                      <div className="text-sm text-gray-400 mb-1">Fat</div>
                      <div className="text-lg font-semibold text-white">25g</div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Cart Slide-in Panel */}
        <AnimatePresence>
          {isCartOpen && (
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 30 }}
              className="fixed inset-y-0 right-0 max-w-xl w-full bg-[#1a1f2e] shadow-xl z-50
                       border-l border-white/10 overflow-hidden"
            >
              <div className="h-full flex flex-col">
                <div className="px-4 py-6 bg-gray-800">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-medium text-pri-white">Your Cart</h2>
                    <button
                      onClick={() => setIsCartOpen(false)}
                      className="text-pri-gray hover:text-pri-white"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto px-4 py-6">
                  {cart.length === 0 ? (
                    <p className="text-pri-gray">Your cart is empty</p>
                  ) : (
                    <ul className="divide-y divide-gray-700">
                      {cart.map((item) => (
                        <li key={item.id} className="py-4 flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-pri-white">{item.name}</p>
                            <p className="text-sm text-pri-gray">£{item.price.toFixed(2)} x {item.quantity}</p>
                          </div>
                          <div className="flex items-center">
                            <button
                              onClick={() => updateQuantity(item.id, item.quantity - 1)}
                              className="text-pri-yellow hover:text-pri-white px-2"
                            >
                              -
                            </button>
                            <span className="text-pri-white mx-2">{item.quantity}</span>
                            <button
                              onClick={() => updateQuantity(item.id, item.quantity + 1)}
                              className="text-pri-yellow hover:text-pri-white px-2"
                            >
                              +
                            </button>
                            <button
                              onClick={() => removeFromCart(item.id)}
                              className="text-pri-red hover:text-pri-white px-2 ml-2"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="px-4 py-6 bg-gray-800">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-lg font-medium text-pri-white">Total</span>
                    <span className="text-lg font-medium text-pri-yellow">£{getTotalPrice()}</span>
                  </div>
                  <button
                    onClick={() => {
                      setIsCheckout(true);
                      setIsCartOpen(false);
                    }}
                    className="w-full bg-pri-yellow text-pri-dark font-semibold py-3 rounded-md hover:bg-opacity-80 transition-colors duration-200"
                  >
                    Proceed to Checkout
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Back Confirmation Modal */}
        <AnimatePresence>
          {showBackConfirmation && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 overflow-y-auto flex items-center justify-center p-4"
            >
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity backdrop-blur-sm" aria-hidden="true"></div>

              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

              <div className="inline-block align-bottom bg-sec-dark rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div className="bg-sec-dark px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                      <h3 className="text-lg leading-6 font-medium text-pri-white" id="modal-title">
                        On going back will clear the cart
                      </h3>
                      <div className="mt-2">
                        <p className="text-sm text-pri-gray">
                          Are you sure you want to go back? This action will clear your current cart.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-800 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-pri-yellow text-base font-medium text-pri-dark hover:bg-opacity-80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pri-yellow sm:ml-3 sm:w-auto sm:text-sm"
                    onClick={confirmBack}
                  >
                    Proceed
                  </button>
                  <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                    onClick={() => setShowBackConfirmation(false)}
                  >
                    Stay
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
};

export default Restaurant;
